import { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import SmartImg from './SmartImg.jsx';

export default function GalleryLightbox({
  slides,
  index,
  onClose,
  onNavigate,
  ariaLabel = 'Enlarged gallery photo',
}) {
  const closeRef = useRef(null);

  const len = slides.length;
  const slide = len > 0 && index >= 0 && index < len ? slides[index] : null;

  const goPrev = useCallback(() => {
    if (len === 0) return;
    const i = index === 0 ? len - 1 : index - 1;
    onNavigate(i);
  }, [index, len, onNavigate]);

  const goNext = useCallback(() => {
    if (len === 0) return;
    const i = index === len - 1 ? 0 : index + 1;
    onNavigate(i);
  }, [index, len, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  if (slide == null) return null;

  return createPortal(
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={ariaLabel}>
      <button
        type="button"
        className="lightbox-backdrop"
        onClick={onClose}
        aria-label="Close gallery"
      />
      <div className="lightbox-inner">
        <button
          ref={closeRef}
          type="button"
          className="lightbox-close"
          onClick={onClose}
          aria-label="Close gallery"
        >
          <span aria-hidden="true">×</span>
        </button>
        <button
          type="button"
          className="lightbox-arrow lightbox-arrow--prev"
          onClick={goPrev}
          aria-label="Previous photo"
        >
          <span aria-hidden="true">‹</span>
        </button>
        <button
          type="button"
          className="lightbox-arrow lightbox-arrow--next"
          onClick={goNext}
          aria-label="Next photo"
        >
          <span aria-hidden="true">›</span>
        </button>
        <figure className="lightbox-stage">
          <div className="lightbox-img-wrap">
            {slide.caption ? (
              <span className="lightbox-caption-badge">{slide.caption}</span>
            ) : null}
            <SmartImg
              key={`${slide.src}-${index}`}
              src={slide.src}
              alt={slide.caption ? `${slide.caption}: ${slide.alt}` : slide.alt}
              className="lightbox-img"
            />
          </div>
          <figcaption className="lightbox-figcaption">
            {slide.caption ? <span className="lightbox-caption-text">{slide.caption}</span> : null}
            <span className="lightbox-counter">
              {index + 1} <span className="lightbox-counter-sep">/</span> {len}
            </span>
          </figcaption>
        </figure>
      </div>
    </div>,
    document.body
  );
}
